import {
  CaseCreationPolicy,
  CaseForm,
  CasePriority,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { TaxonomyNatureOption, TaxonomySelection, TaxonomyTask } from './CaseTaxonomy.types'

// Natures live on the deepest node of each branch (service type, category, or
// subcategory) and are only selectable once that node is reached.
export const naturesForSelection = ({
  serviceType,
  category,
  subcategory,
}: TaxonomySelection): TaxonomyNatureOption[] => {
  if (subcategory) {
    return subcategory.natures
  }
  if (category) {
    return category.subcategories.length ? [] : category.natures
  }
  if (serviceType) {
    return serviceType.categories.length ? [] : serviceType.natures
  }
  return []
}

export const findSelectionByTaxonomyId = (
  tasks: TaxonomyTask[],
  taxonomyId: string,
): TaxonomySelection | undefined => {
  for (const task of tasks) {
    for (const serviceType of task.serviceTypes) {
      const serviceTypeNature = serviceType.natures.find(nature => nature.taxonomyId === taxonomyId)
      if (serviceTypeNature) {
        return { task, serviceType, nature: serviceTypeNature }
      }

      for (const category of serviceType.categories) {
        const categoryNature = category.natures.find(nature => nature.taxonomyId === taxonomyId)
        if (categoryNature) {
          return { task, serviceType, category, nature: categoryNature }
        }

        for (const subcategory of category.subcategories) {
          const subcategoryNature = subcategory.natures.find(
            nature => nature.taxonomyId === taxonomyId,
          )
          if (subcategoryNature) {
            return { task, serviceType, category, subcategory, nature: subcategoryNature }
          }
        }
      }
    }
  }
  return undefined
}

export const resolveSingleOptions = (selection: TaxonomySelection): TaxonomySelection => {
  const { task } = selection
  let { serviceType, category, subcategory } = selection
  if (task && !serviceType && task.serviceTypes.length === 1) {
    serviceType = task.serviceTypes[0]
  }
  if (serviceType && !category && serviceType.categories.length === 1) {
    category = serviceType.categories[0]
  }
  if (category && !subcategory && category.subcategories.length === 1) {
    subcategory = category.subcategories[0]
  }
  return { ...selection, task, serviceType, category, subcategory }
}

/** Natures arrive as enum values (REPAIR); node labels are already display-ready. */
export const natureLabel = (nature: TaxonomyNatureOption) =>
  nature.nature.charAt(0) + nature.nature.slice(1).toLowerCase()

interface FilterTaxonomyNaturesParams {
  tasks: TaxonomyTask[]
  keepNature: (nature: TaxonomyNatureOption) => boolean
}

/**
 * The tree reduced to the natures `keepNature` accepts. A branch left without
 * a nature and without children is dropped: picking it would lead nowhere.
 */
const filterTaxonomyNatures = ({
  tasks,
  keepNature,
}: FilterTaxonomyNaturesParams): TaxonomyTask[] => {
  const keepNatures = (natures: TaxonomyNatureOption[]) => natures.filter(keepNature)

  return tasks.flatMap(task => {
    const serviceTypes = task.serviceTypes.flatMap(serviceType => {
      const categories = serviceType.categories.flatMap(category => {
        const subcategories = category.subcategories
          .map(subcategory => ({ ...subcategory, natures: keepNatures(subcategory.natures) }))
          .filter(subcategory => subcategory.natures.length > 0)
        const natures = keepNatures(category.natures)
        return subcategories.length || natures.length
          ? [{ ...category, subcategories, natures }]
          : []
      })
      const natures = keepNatures(serviceType.natures)
      return categories.length || natures.length ? [{ ...serviceType, categories, natures }] : []
    })
    return serviceTypes.length ? [{ ...task, serviceTypes }] : []
  })
}

/**
 * The tree reduced to the natures a single case form can persist, so a Field
 * Ops form never offers a Housekeeping or Inspection classification.
 */
export const filterTasksByCaseForm = (tasks: TaxonomyTask[], caseForm: CaseForm): TaxonomyTask[] =>
  filterTaxonomyNatures({ tasks, keepNature: nature => nature.caseForm === caseForm })

/**
 * The tree a person may create a case from: classifications that only
 * automations create are left out, and so is every branch they leave empty.
 */
export const manuallyCreatableTaxonomy = (tasks: TaxonomyTask[]): TaxonomyTask[] =>
  filterTaxonomyNatures({
    tasks,
    keepNature: nature => nature.creationPolicy !== CaseCreationPolicy.automatedOnly,
  })

export const priorityFromLevel = (level?: string | null): CasePriority | undefined =>
  Object.values(CasePriority).includes(level as CasePriority) ? (level as CasePriority) : undefined

export const DEFAULT_TAXONOMY_STATUS_LABEL = 'New'

export const defaultStatusForTask = (task: TaxonomyTask | null | undefined) =>
  task?.statuses.find(status => status.label === DEFAULT_TAXONOMY_STATUS_LABEL)

/** Every node's label by its id, across all four levels. */
export const collectTaxonomyNodeLabels = (tasks: TaxonomyTask[]): Map<string, string> => {
  const labelsByNodeId = new Map<string, string>()

  const addNode = (node: { taxonomyNodeId: string; label: string }) => {
    labelsByNodeId.set(node.taxonomyNodeId, node.label)
  }

  tasks.forEach(task => {
    addNode(task)
    task.serviceTypes.forEach(serviceType => {
      addNode(serviceType)
      serviceType.categories.forEach(category => {
        addNode(category)
        category.subcategories.forEach(addNode)
      })
    })
  })

  return labelsByNodeId
}
